import type { Cents } from "../types/money";

/**
 * Zakelijke staffelkorting ("Glastotaal Zakelijk"). Eén zakelijke status, géén
 * niveaus/labels — de korting groeit met het totaal besteed (excl. btw) in het
 * lopende kalenderjaar en reset op 1 januari. De server past dit toe in
 * createOrder; de client gebruikt dezelfde tabel alleen om de voortgang te tonen.
 *
 * Drempels afgestemd: vanaf €1.000 loopt de korting op tot maximaal 15%.
 */
export interface B2bTier {
  /** Ondergrens totaal besteed dit kalenderjaar (excl. btw) in centen. */
  minSpend: Cents;
  /** Kortingspercentage vanaf deze drempel. */
  percent: number;
}

export const B2B_TIERS: readonly B2bTier[] = [
  { minSpend: 0, percent: 0 },
  { minSpend: 100_000, percent: 5 }, // €1.000
  { minSpend: 500_000, percent: 10 }, // €5.000
  { minSpend: 1_000_000, percent: 15 }, // €10.000
] as const;

/** Hoogst haalbare staffelkorting (voor koptekst/voortgangsbalk). */
export const B2B_TIER_MAX_PERCENT = 15;

export interface B2bTierStatus {
  /** Huidig kortingspercentage bij deze jaaromzet. */
  percent: number;
  /** Totaal besteed dit kalenderjaar waarop dit is gebaseerd (centen). */
  spend: Cents;
  /** Volgende drempel (centen) waarboven het percentage stijgt; null bij max. */
  nextAt: Cents | null;
  /** Percentage dat bij die volgende drempel geldt; null bij max. */
  nextPercent: number | null;
}

/** Bepaal het staffel-percentage (+ eerstvolgende drempel) bij een bestede som. */
/**
 * Wat één order meetelt voor de staffel: het orderbedrag EXCLUSIEF btw.
 *
 * De drempels hierboven zijn altijd al als "excl. btw" bedoeld, maar zowel de client als
 * createOrder telde het incl.-btw-totaal op. Daardoor haalde een zakelijke klant elke drempel
 * ~21% te vroeg — bij een drempel van €1.000 al na €826 werkelijke omzet.
 *
 * `total - vat` en niet `subtotal`: dit is het hele orderbedrag excl. btw, dus inclusief
 * verzendkosten. Ontbreekt `vat` (heel oude orders), dan rekenen we het terug uit de
 * onderdelen die er wél zijn.
 */
export function orderSpendExVatCents(totals: {
  total?: number;
  vat?: number;
  subtotal?: number;
  discount?: number;
  shipping?: number;
} | null | undefined): Cents {
  if (!totals) return 0 as Cents;
  const total = Number(totals.total ?? 0);
  const vat = Number(totals.vat ?? NaN);
  if (Number.isFinite(vat)) return Math.max(0, Math.round(total - vat)) as Cents;
  const sub = Number(totals.subtotal ?? 0);
  const korting = Number(totals.discount ?? 0);
  const verzending = Number(totals.shipping ?? 0);
  return Math.max(0, Math.round(sub - korting + verzending)) as Cents;
}

export function b2bTierForSpend(spendCents: Cents): B2bTierStatus {
  let current: B2bTier = B2B_TIERS[0]!;
  let next: B2bTier | null = null;
  for (const t of B2B_TIERS) {
    if (spendCents >= t.minSpend) current = t;
    else {
      next = t;
      break;
    }
  }
  return {
    percent: current.percent,
    spend: spendCents,
    nextAt: next ? next.minSpend : null,
    nextPercent: next ? next.percent : null,
  };
}
